import React, { useState, useMemo } from 'react';
import JsonView from '@uiw/react-json-view';
import { X, Copy, Search, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useViewerStore, useViewerCurrentPage } from '@/stores/viewer/viewer-store';

interface ViewerDebugPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ViewerDebugPanel: React.FC<ViewerDebugPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);

  // Get current page data from viewer store
  const currentPage = useViewerCurrentPage();
  const components = useViewerStore((state) => state.components);

  // Combine page and components into debug data
  const debugData = useMemo(() => ({
    page: currentPage ? {
      id: currentPage.id,
      name: currentPage.name,
      description: currentPage.description,
      order: currentPage.order,
      isDefault: currentPage.isDefault,
      template: currentPage.template,
      navigation: currentPage.navigation,
      permissions: currentPage.permissions,
      canvasSettings: currentPage.canvasSettings,
      metadata: currentPage.metadata,
      createdAt: currentPage.createdAt,
      updatedAt: currentPage.updatedAt,
    } : null,
    components: components,
    componentCount: components.length,
  }), [currentPage, components]);

  // Filter JSON data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return debugData;

    const jsonString = JSON.stringify(debugData, null, 2);
    const searchLower = searchTerm.toLowerCase();

    // Simple search - we'll highlight in the JSON view
    if (jsonString.toLowerCase().includes(searchLower)) {
      return debugData;
    }

    return debugData;
  }, [debugData, searchTerm]);

  // Copy JSON to clipboard
  const handleCopy = async () => {
    try {
      const jsonString = JSON.stringify(debugData, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy JSON:', error);
    }
  };

  // Count search matches
  const matchCount = useMemo(() => {
    if (!searchTerm) return 0;
    const jsonString = JSON.stringify(debugData, null, 2).toLowerCase();
    const matches = jsonString.match(new RegExp(searchTerm.toLowerCase(), 'g'));
    return matches ? matches.length : 0;
  }, [debugData, searchTerm]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sliding panel from right */}
      <aside
        className={cn(
          'fixed top-0 right-0 h-full w-[600px] bg-background border-l border-border z-[70]',
          'transform transition-transform duration-300 ease-in-out shadow-xl',
          'flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <h2 className="text-lg font-semibold">Page JSON Debug</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Search bar */}
        <div className="px-4 py-3 border-b border-border bg-muted/20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search JSON..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          {searchTerm && (
            <p className="text-xs text-muted-foreground mt-2">
              {matchCount} {matchCount === 1 ? 'match' : 'matches'} found
            </p>
          )}
        </div>

        {/* JSON content */}
        <div className="flex-1 overflow-auto p-4">
          <JsonView
            value={filteredData}
            displayDataTypes={false}
            displayObjectSize={true}
            enableClipboard={false}
            collapsed={2}
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '12px',
              lineHeight: '1.5',
              backgroundColor: 'transparent',
            }}
          />
        </div>

        {/* Footer with stats */}
        <div className="px-4 py-2 border-t border-border bg-muted/20 text-xs text-muted-foreground">
          <div className="flex justify-between">
            <span>Page: {currentPage?.name || 'No page loaded'}</span>
            <span>{components.length} components</span>
          </div>
        </div>
      </aside>
    </>
  );
};
